import isArray from "lodash/isArray";
import isFunction from "lodash/isFunction";
import isEqualWith from "lodash/isEqualWith";
import isObject from "lodash/isObject";
import moment from "moment";
import { ArgumentError } from "@ms/uno-errors/lib/local/errors";
// Import Temporal polyfill - adjust import path if needed
import { Temporal } from "temporal-polyfill-lite";
/**
 * Compares the source and target and returns an object corresponding to target - source
 * This only works reliably with objects with primitive contents
 * @param source The base to diff against
 * @param target The updated object to compute the diff with
 * @param customDiff An optional diff function that is passed the source value, target value, and key of all values in the diffed objects. It should return the diff for these values.
 * @param getTargetNewPropertiesDiff If true, properties that are in target but not in source will be included in the diff. Default is false.
 * If the function returns undefined, the default deep comparison will be used. If the function returns an empty object, then the key will be skipped (meaning no diff)
 */ export function getDiff(source, target, customDiff, getTargetNewPropertiesDiff) {
    "use strict";
    if (target == null) {
        return {};
    }
    if (source == null) {
        return target;
    }
    // In Typescript, numbers and strings are still 'Objects', and so can be passed to this function. Throw if that happens
    if (!(isObject(source) && isObject(target))) {
        throw new ArgumentError("source/target", "Both source and target must be objects. Source: " + JSON.stringify(source) + ". Target: " + JSON.stringify(target));
    }
    const returnObject = {};
    let keys = Object.keys(source);
    if (getTargetNewPropertiesDiff === true) {
        // To include properties that are in target but not in source, we need to iterate over all keys in both source and target
        keys = Array.from(new Set([
            ...keys,
            ...Object.keys(target)
        ]));
    }
    keys.forEach((key)=>{
        const value = source[key];
        // If a customDiff function was passed in, try to use that
        if (customDiff) {
            const diff = customDiff(value, target[key], key);
            // If the diff function returned a value other than undefined, use it
            if (diff !== undefined) {
                // If the diff function returned an empty object, then skip this key
                if (!isEqual(diff, {})) {
                    returnObject[key] = diff;
                }
                return;
            }
        }
        // Use the default diff method
        if (isFunction(target[key]) || target[key] === value) {
            return;
        }
        // If the value is an array, we need to check if the arrays are equal
        if (isArray(value) && isArray(target[key])) {
            if (!isEqual(JSON.stringify(value), JSON.stringify(target[key]))) {
                returnObject[key] = target[key];
            }
            return;
        }
        if (isObject(value) && isObject(target[key])) {
            const diff = getDiff(value, target[key], customDiff, getTargetNewPropertiesDiff);
            // If the diff function returned an empty object, then skip this key
            if (!isEqual(diff, {})) {
                returnObject[key] = diff;
            }
            return;
        }
        returnObject[key] = target[key];
    });
    return returnObject;
}
/**
 * Customizer function to be used with getDiff. Checks to see if the diffed value is a moment. If it is, the function will return target if the
 * moments are different or an empty diff if they are the same
 */ export function getDiffMomentCustomizer(source, target, key) {
    if (moment.isMoment(source) || moment.isMoment(target)) {
        // If either of these values are moments, then we should handle this scenario
        if (moment.isMoment(source) && moment.isMoment(target)) {
            if (source.valueOf() === target.valueOf()) {
                // If both are valid moments and are the same, then we don't add to diff set.
                return {};
            }
        }
        return target;
    } else {
        // If neither of these are moments, let the default differ handle this
        return undefined;
    }
}
/**
 * Customizer function to be used with lodash merge. Checks to see if the source is a moment, and returns the target if it is.
 */ export function applyDiffMomentCustomizer(source, target, key) {
    if (isFunction(source)) {
        return source;
    }
    if (moment.isMoment(source)) {
        return target;
    }
}
/**
 * Helper function to check if a value is a Temporal object
 * @param value Value to check
 * @returns True if the value is a Temporal object, false otherwise
 */ function isTemporal(value) {
    return value && typeof value === "object" && (value instanceof Temporal.Instant || value instanceof Temporal.ZonedDateTime || value instanceof Temporal.PlainDateTime || value instanceof Temporal.PlainTime || value instanceof Temporal.PlainDate);
}
/**
 * Customizer function to be used with getDiff. Checks to see if the diffed value is a Temporal object. If it is, the function will return target if the
 * Temporal objects are different or an empty diff if they are the same
 * @param source Source value
 * @param target Target value
 * @param key Key of the value
 * @returns Target value if the source is a Temporal object, an empty object if the Temporal objects are the same, or undefined otherwise
 */ export function getDiffTemporalCustomizer(source, target, key) {
    if (isTemporal(source) || isTemporal(target)) {
        // If either of these values are Temporal objects, then we should handle this scenario
        if (isTemporal(source) && isTemporal(target)) {
            try {
                // Compare the Temporal objects if they're of the same type
                if (source.constructor === target.constructor && source.equals(target)) {
                    // If both are valid Temporal objects of the same type and are equal, then we don't add to diff set.
                    return {};
                }
            } catch (e) {
            // If comparison fails, return target
            }
        }
        return target;
    } else {
        // If neither of these are Temporal objects, let the default differ handle this
        return undefined;
    }
}
/**
 * Helper customizer function for lodash's `isEqualWith` method to handle Temporal objects.
 *
 * This function provides a specialized comparison for Temporal objects, ensuring
 * that they are compared using their `.equals()` method. If the values are not
 * Temporal objects, it returns `undefined` to allow lodash's default comparison logic.
 *
 * @param a - The first value to compare.
 * @param b - The second value to compare.
 * @returns `true` if the Temporal objects are equal, `false` if they are not,
 *          or `undefined` if the values are not Temporal objects.
 *
 */ function isEqualTemporalCustomizer(a, b) {
    if (isTemporal(a)) {
        return isTemporal(b) && a.constructor === b.constructor && a.equals(b);
    }
    return undefined;
}
/**
 * Compares two values for equality, with special handling for Temporal objects.
 *
 * This function uses lodash's `isEqualWith` method to perform a deep comparison
 * between the two values, while correctly handling Temporal equality comparison
 * (which lodash does not do).
 *
 * @param a - The first value to compare.
 * @param b - The second value to compare.
 * @returns `true` if the values are equal, `false` otherwise.
 */ export function isEqual(a, b) {
    return isEqualWith(a, b, isEqualTemporalCustomizer);
}
/**
 * Customizer function to be used with lodash merge. Checks to see if the source is a Temporal object, and returns the target if it is.
 * @param source Source value
 * @param target Target value
 * @param key Key of the value
 * @returns Target value if the source is a Temporal object, or the source value otherwise
 */ export function applyDiffTemporalCustomizer(source, target, key) {
    if (isFunction(source)) {
        return source;
    }
    if (isTemporal(source)) {
        return target;
    }
}
/**
 * Customizer function to be used with getDiff for arrays. This function will perform
 * a simple replacement when dealing with arrays, treating them as atomic values rather
 * than merging their contents.
 * @param source Source value (may be an array)
 * @param target Target value (may be an array)
 * @param key Key of the property being compared
 * @returns Target value when arrays are involved, or undefined to allow default handling for non-arrays
 */ export function getDiffArrayOfObjectsCustomizer(source, target, key) {
    if (isArray(source) && isArray(target)) {
        if (target == null) {
            return target; // null/undefined target explicit replacement
        }
        // If arrays are equal (stringify for simple structural equality), suppress diff
        if (isEqual(JSON.stringify(source), JSON.stringify(target))) {
            return {}; // indicate no diff for this key
        }
        return target; // arrays differ -> replace
    }
    // If only one is an array, treat as replacement
    if (isArray(source) || isArray(target)) {
        return target;
    }
    return undefined; // non-arrays -> default handling
}
/**
 * Customizer function to be used with lodash merge for arrays of objects. This function will
 * merge arrays by replacing the entire array when dealing with arrays of objects.
 * @param source Source value
 * @param target Target value
 * @param key Key of the value
 * @returns Target array if source is an array of objects, undefined for default handling
 */ export function applyDiffArrayOfObjectsCustomizer(source, target, key) {
    if (isFunction(source)) {
        return source;
    }
    // Handle arrays - replace entire array for arrays of objects
    if (isArray(source)) {
        return target;
    }
    // For non-arrays, let default handling work
    return undefined;
}
/**
 * Combined customizer for getDiff that first attempts Temporal comparison, then array-of-objects handling.
 * Exported to avoid re-creating per-call combined functions across entity helpers.
 * If neither specialized path applies, returns undefined to allow default diff logic.
 *
 * Order matters: Temporal check precedes array handling so Temporal objects wrapped in arrays are treated atomically by the array branch.
 */ export function getDiffTemporalAndArrayCustomizer(source, target, key) {
    const temporalResult = getDiffTemporalCustomizer(source, target, key);
    if (temporalResult !== undefined) {
        return temporalResult;
    }
    const arrayResult = getDiffArrayOfObjectsCustomizer(source, target, key);
    if (arrayResult !== undefined) {
        return arrayResult;
    }
    return undefined;
}
/**
 * Combined customizer for lodash merge (applyDiff) that first attempts Temporal replacement,
 * then array-of-objects replacement. Falls through to default merge behavior when neither applies.
 */ export function applyDiffTemporalAndArrayCustomizer(source, target, key) {
    const temporalResult = applyDiffTemporalCustomizer(source, target, key);
    if (temporalResult !== undefined) {
        return temporalResult;
    }
    const arrayResult = applyDiffArrayOfObjectsCustomizer(source, target, key);
    if (arrayResult !== undefined) {
        return arrayResult;
    }
    return undefined;
}
/**
 * Returns a new object with only all the truthy values from the input object
 * @param source Reference object
 */ export function stripOutFalseyValues(source) {
    if (source == null) {
        return null;
    }
    // use getDiff() method, passing in our object for both source and target and specifying a custom diff method
    return getDiff(source, source, (source, target, key)=>{
        if (target) {
            // if truthy, return the target
            return target;
        } else {
            // if falsey, return empty object which tells diff method to skip this key (i.e. omit)
            return {};
        }
    });
}
/**
 * Safe JSON parse
 * @param jsonString JSON string
 * @param fallback Fallback value
 * @returns Parsed JSON or fallback value
 */ export function safeJSONParse(jsonString, fallback = {}) {
    try {
        if (jsonString) {
            return JSON.parse(jsonString);
        }
        return fallback;
    } catch (error) {
        return fallback;
    }
}
